import { useRef, useEffect, useState, useContext } from 'react'

import Nav from './nav'
import Image from '../basic/image'
import ScrollAnimation from '../layout/scroll_animation'
import AnnouncementBar from './announcement_bar'

import styles from './header.module.sass'

import classNames from 'classnames'

import get from 'lodash/get'

import { AnnouncementContext } from '../context/announcementContext'

const globalData = JSON.parse(document.getElementById('global-data').innerHTML)
const navData = JSON.parse(document.getElementById('nav-data').innerHTML.replace('&#038;', '&'))
const pageData = JSON.parse(document.getElementById('page-data').innerHTML)

function Header() {
	const headerRef = useRef(null)
	const containerRef = useRef(null)
	const hamburgerRef = useRef(null)
	const searchButtonRef = useRef(null)
	const searchInputRef = useRef(null)
	const searchPopupRef = useRef(null)
	const [searchOpen, setSearchOpen] = useState(false)
	const [defaultTheme, setDefaultTheme] = useState(get(pageData, 'page_settings.header_style', 'solid'))
	const [theme, setTheme] = useState(get(pageData, 'page_settings.header_style', 'solid'))
	const [headerHeight, setHeaderHeight] = useState(null)
	const [mobileExpanded, setMobileExpanded] = useState(false)
	const [desktopOpen, setDesktopOpen] = useState(false)
	const [compact, setCompact] = useState(false)
	const [collapseNav, setCollapseNav] = useState(false)
	const {announcementHeight, heightChangeHandler} = useContext(AnnouncementContext)
	const mouseLeaveTimeout = useRef()
	const hostname = window.location.hostname
	const homeURL = (hostname == 'www.personalis.com' || hostname == 'investors.personalis.com') ? 'https://www.personalis.com/' : '/'

	const isMobile = () => {
		if (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) < 768)
			return true
		return false
	}

	useEffect(() => {
		const header =  headerRef.current

		header?.addEventListener('mouseenter', toggleActiveDesktop)
		header?.addEventListener('mouseleave', toggleActiveDesktop)
		hamburgerRef.current?.addEventListener('click', toggleMobileMenu)
		window.addEventListener('scroll', handleScroll)
		searchButtonRef.current?.addEventListener('click', toggleSearch)

		return () => {
			clearTimeout(mouseLeaveTimeout.current)
			header?.removeEventListener('mouseenter', toggleActiveDesktop)
			header?.removeEventListener('mouseleave', toggleActiveDesktop)
			hamburgerRef.current?.removeEventListener('click', toggleMobileMenu)
			window.removeEventListener('scroll', handleScroll)
			searchButtonRef.current?.removeEventListener('click', toggleSearch)
		}
	}, [searchOpen, compact, mobileExpanded, desktopOpen, announcementHeight, mouseLeaveTimeout.current])

	useEffect(() => {
		setTheme(defaultTheme)

		if (containerRef.current) {
			setHeaderHeight(containerRef.current.offsetHeight)
		}

		const headerSizeTimeout = setTimeout(() => {
			if (containerRef.current) {
				setHeaderHeight(containerRef.current.offsetHeight)
			}
		}, 4000)

		return () => clearTimeout(headerSizeTimeout)

	}, [])

	const toggleMobileMenu = () => {
		if(!isMobile())
			return
		
		if(mobileExpanded){
			document.body.classList.remove('overlay_open')
		} else {
			document.body.classList.add('overlay_open')
		}
		
		setMobileExpanded(value => !value)
		
		if(searchOpen){
			setSearchOpen(false)
		}
	}

	const toggleActiveDesktop = (e) => {
		if(isMobile())
			return

		if (e.type == 'mouseleave') {
			if(searchOpen){
				return
			}

			headerRef.current.style.minHeight = null

			if(document.documentElement.scrollTop > 120){
				setTheme('solid')
			} else {
				mouseLeaveTimeout.current = window.setTimeout(() => {setTheme(defaultTheme), 500})
			}

			setCollapseNav(true)
			setDesktopOpen(false)
		} else {
			if(mouseLeaveTimeout.current){
				clearTimeout(mouseLeaveTimeout.current)
				mouseLeaveTimeout.current = null
			}
			setTheme('solid')
			setDesktopOpen(true)
			setCollapseNav(false)
		}
	}

	const handleScroll = () => {
		const subnavHeight = document.querySelector("[expanded='true']")?.offsetHeight
		let compactOffset = 120
		const frontHero = document.getElementById('front_hero')

		if(desktopOpen){
			return false
		}

		if(document.body.classList.contains('home')){
			compactOffset = window.innerHeight - 300	
		}

		if(document.documentElement.scrollTop > compactOffset || mobileExpanded){
			setTheme('solid')
			setTimeout(() => {
				setHeaderHeight(containerRef.current?.offsetHeight)
			}, 500)
		} else {
			setTheme(defaultTheme)
			setTimeout(() => {
				setHeaderHeight(containerRef.current?.offsetHeight)
			}, 500)
		}

		if(document.documentElement.scrollTop > compactOffset){
			headerRef.current?.classList.add(styles.compact)
			setCompact(true)
		} else {
			headerRef.current?.classList.remove(styles.compact)
			setCompact(false)
		}
	}

	const toggleSearch = (e) => {
		e?.preventDefault()

		if(searchOpen){
			setSearchOpen(false)
			setCollapseNav(false)
			headerRef.current.style.minHeight = null

			if(isMobile()){
				document.body.classList.remove('overlay_open')
	
				setTimeout(() => {
					if(document.documentElement.scrollTop > 120){
						setTheme('solid')
					} else {
						setTheme(defaultTheme)
					}
				}, 500)
			}
		} else {
			setSearchOpen(true)
			setCollapseNav(true)

			headerRef.current.style.minHeight = null
			
			if(isMobile()){
				setTheme('solid')
				document.body.classList.add('overlay_open')
			}
		}

		if(mobileExpanded){
			setMobileExpanded(false)
		}
	}

	function doSearch(e) {
		e.preventDefault()
		window.location = `/search?query=${searchInputRef.current.value}`
	}

	useEffect(() => {
		setTimeout(() => {
			setHeaderHeight(containerRef.current.offsetHeight)
		}, 500)
	}, [announcementHeight])

	function closeSearch() {
		setSearchOpen(false)
	}

	const handleCollapseNav = () => {
		setCollapseNav(true)
	}

	useEffect(() => {
		setCollapseNav(false)
	}, [collapseNav])

	return <div ref={headerRef} className={classNames(styles.header, styles[theme], desktopOpen ? styles.desktop_open : '', mobileExpanded ? styles.expanded : '', searchOpen ? styles.search_open : '', compact ? styles.compact : '')} height={headerHeight} data-header>
		<ScrollAnimation styles={styles} hide={styles.hide} show={styles.show}>
			<div ref={containerRef} className={styles.header_container}>
				<AnnouncementBar className={styles.announcement_bar} />
				<div className={styles.header_wrapper}>
					<div className={styles.secondary_nav_wrapper}>
						<Nav items={navData.secondary_items} type='secondary' theme={theme} className={styles.secondary} />
					</div>
					<hr />
					<div className={styles.main_nav_wrapper}>
						<a href={homeURL} aria-label='Personalis home' className={styles.logo_wrapper}>
							<Image {...globalData?.global_settings?.logo} settings={{loading: 'eager', transition: 'none'}} className={styles.primary_logo} />
							<Image {...globalData?.global_settings?.secondary_logo} settings={{loading: 'eager', transition: 'none'}} className={styles.secondary_logo} />
						</a>
						<button ref={hamburgerRef} id='main_menu_toggle' className={classNames(styles.main_menu_toggle)} title="Toggle main menu">
							<span className={classNames(styles.hamburger, styles.black, styles.toggle_image)}></span>
							<span className={classNames(styles.hamburger, styles.white, styles.toggle_image)}></span>
							<span className={classNames(styles.close, styles.toggle_image)}></span>
							{/* <span className="screenreader_only">Toggle main menu</span> */}
						</button>
						<div className={styles.nav_wrapper}>
							<div className={styles.main_row}>
								<Nav items={navData.main_items} type='main' theme={theme} compact={compact} collapse={collapseNav} collapseNav={handleCollapseNav} mobileExpanded={mobileExpanded} handleSearch={closeSearch} />
								<button ref={searchButtonRef} aria-label='open search' className={classNames(searchOpen ? styles.active : '', styles.open_search)}></button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ScrollAnimation>
		<form onSubmit={doSearch} ref={searchPopupRef} className={classNames(searchOpen ? styles.open : '', styles.search_popup)}>
			<h2>SEARCH</h2> <input placeholder="Enter keyword to search site..." ref={searchInputRef} type="text" /> <button type="submit" value="Submit">Submit</button>
		</form>
	</div>
}

export default Header